import { ActionWithoutArgs } from "@excentone/spfx-utilities";
import { useBoolean } from "@fluentui/react-hooks";
import { isEqual } from "@microsoft/sp-lodash-subset";
import {
  IPnPClientStore,
  PnPClientStorage
} from "@pnp/common";
import { DateTime } from "luxon";
import {
  useMemo,
  Dispatch,
  useState,
  useEffect,
  useCallback,
  SetStateAction,
} from "react";

export interface IPnpClientStorageOperation<T> {
  setState: Dispatch<SetStateAction<Partial<T>>>;
  resetState: ActionWithoutArgs;
  getOrInitState: () => Promise<Partial<T>>;
}

export type PnpClientStorageHook<T> = readonly [Partial<T>, IPnpClientStorageOperation<T>];

const usePnpClientStorage = <T>(
  cacheKey: string,
  storageName: keyof PnPClientStorage,
  initialState: Partial<T>,
  selectStore: (storage: PnPClientStorage) => IPnPClientStore,
  expire?: Date): PnpClientStorageHook<T> => {

  const [state, setState] = useState<Partial<T>>(initialState);
  const [initializing, { setFalse: initializationDone }] = useBoolean(true);

  const storage = useMemo(
    () => selectStore(new PnPClientStorage()),
    [selectStore]
  );

  const expiration = useMemo(
    (): Date => {
      if (!expire) {
        expire = DateTime.now().plus({ 'hours': 12 }).toJSDate();
        const dt = DateTime.fromJSDate(expire);
        console.warn(`[${cacheKey}]: No expiration date set, will use the default expiration. Cached data will expire ${dt.diffNow('hours')}: ${dt.toFormat('YYYY-MM-DD HH:mm')}`);
      }

      return expire;
    },
    [expire]
  );

  const resetState = useCallback(
    () => setState({ ...initialState }),
    [initialState]
  );

  const getOrInitState = useCallback(
    () => storage.getOrPut(cacheKey, () => Promise.resolve(initialState), expiration),
    [storage, cacheKey, initialState, expiration]
  );


  useEffect(
    () => {
      storage.deleteExpired();

      if (initializing && cacheKey) {

        if (!storage.enabled) {
          console.info(`[${cacheKey}]: "${storageName}" storage is disabled. Persisting search parameters will not work.`);
          return;
        }

        console.info(`[${cacheKey}]: Initial State: `, initialState);

        getOrInitState()
          .then(setState)
          .finally(initializationDone);
      }
    },
    [cacheKey, initialState]
  );

  useEffect(
    () => {
      if (!initializing && state) {
        const cachedState = storage.get(cacheKey);

        if (cachedState !== state || !isEqual(cachedState, state))
          storage.put(cacheKey, state, expiration);
      }
    },
    [cacheKey, state]
  );

  return [state, { setState, resetState, getOrInitState } as const];
};

export const usePnpLocalStorage = <T>(key: string, initialValue: Partial<T>, expire?: Date) => usePnpClientStorage(key, 'local', initialValue, storage => storage.local, expire);
export const usePnpSessionStorage = <T>(key: string, initialValue: Partial<T>, expire?: Date) => usePnpClientStorage(key, 'session', initialValue, storage => storage.session, expire);
